import { useEffect, useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { heroSlides } from "@/data/heroSlides";

/**
 * Crossfading background slideshow for the hero. Shows real photos when
 * provided (see src/data/heroSlides.ts), otherwise renders dark industrial
 * "concrete" panels so the hero looks finished pre-photography. Auto-advances
 * only when motion is allowed; slides are decorative, dots are operable.
 */
export function HeroSlideshow() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || heroSlides.length < 2) return;
    const id = window.setInterval(() => {
      setIndex((p) => (p + 1) % heroSlides.length);
    }, 5500);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="hslide">
      {heroSlides.map((slide, i) => (
        <div
          key={i}
          className={`hslide__item hslide__item--${i % 3} ${i === index ? "is-active" : ""}`}
          aria-hidden="true"
        >
          {slide.image ? (
            <picture>
              <source type="image/webp" srcSet={slide.image.webpSrcSet} sizes="100vw" />
              <img
                src={slide.image.fallback}
                alt=""
                loading={i === 0 ? "eager" : "lazy"}
                decoding="async"
              />
            </picture>
          ) : (
            <span className="hslide__wm">
              <Icon name="blade" size={320} />
            </span>
          )}
        </div>
      ))}

      {heroSlides.length > 1 && (
        <div className="hslide__dots" role="tablist" aria-label="Hero images">
          {heroSlides.map((slide, i) => (
            <button
              key={i}
              type="button"
              className={i === index ? "is-active" : ""}
              aria-label={slide.alt}
              aria-selected={i === index}
              role="tab"
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
      )}

      <style>{`
        .hslide { position: absolute; inset: 0; overflow: hidden; z-index: 0; }
        .hslide__item {
          position: absolute; inset: 0;
          opacity: 0;
          transition: opacity 1200ms var(--ease-out);
          display: flex; align-items: center; justify-content: center;
        }
        .hslide__item.is-active { opacity: 1; }
        .hslide__item picture { display: block; width: 100%; height: 100%; }
        .hslide__item img { width: 100%; height: 100%; object-fit: cover; }
        /* Industrial placeholder slides (no photo yet) */
        .hslide__item--0 { background: linear-gradient(135deg, #2c2a27 0%, #201f1d 55%, #181715 100%); }
        .hslide__item--1 { background: linear-gradient(120deg, #302d29 0%, #232120 55%, #181715 100%); }
        .hslide__item--2 { background: linear-gradient(150deg, #2a2825 0%, #201f1d 55%, #181715 100%); }
        .hslide__item::before {
          content: ""; position: absolute; inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px);
          background-size: 46px 46px;
          -webkit-mask-image: radial-gradient(90% 90% at 60% 40%, #000, transparent 75%);
          mask-image: radial-gradient(90% 90% at 60% 40%, #000, transparent 75%);
        }
        .hslide__wm { color: var(--orange); opacity: 0.14; transform: rotate(-8deg); }
        .hslide__item--1 .hslide__wm { transform: rotate(6deg) translateX(40px); }
        .hslide__item--2 .hslide__wm { transform: rotate(-14deg) translateX(-30px); }

        .hslide__dots {
          position: absolute; z-index: 3; left: 0; right: 0; bottom: var(--space-5);
          display: flex; justify-content: center; gap: var(--space-2);
        }
        .hslide__dots button {
          width: 28px; height: 4px; border: none; border-radius: 2px;
          background: rgba(255,255,255,0.35); cursor: pointer; padding: 0;
          transition: background var(--duration-fast) var(--ease-standard);
        }
        .hslide__dots button.is-active { background: var(--orange); }
        .hslide__dots button:hover { background: rgba(255,255,255,0.6); }
      `}</style>
    </div>
  );
}
